import * as actionTypes from 'constants/action-types';
import { action } from 'typesafe-actions';
import { ThunkAction, ThunkDispatch } from 'redux-thunk';
import { AnyAction } from 'redux';
import axios from 'axios/axios-base';
import analytics, { getAnalyticsEndpointBaseUrl } from 'axios/axios-analytics';
import { BlazemeterUserConfig } from 'types/rest/blazeMeterApiResponse';
import { showToastNotification } from 'store/ui/toasts/toastsActions';
import { ToastType } from 'store/ui/toasts/toastsTypes';
import MixpanelService from 'utils/mixpanelService';
import { ApplicationState } from 'store/rootReducer';
import { getAllTransactionsThunk, getTransactions } from 'store/transactions/transactionsActions';
import { getLocationsThunk, getMockServiceListThunk } from 'store/mockServices/mockServiceList/mockServiceListActions';
import { fetchServices } from 'store/services/servicesActions';
import { ACCOUNT_CHANGED, WORKSPACE_CHANGED } from 'utils/mixpanelEvents';
import { getMockServiceTemplates } from 'store/mockServiceTemplates/mockServiceTemplatesActions';
import i18n from 'i18n';
import _ from 'lodash';
import { getMarFileListThunk } from 'store/marFiles/marFilesActions';
import {
  buildPendoData,
  getBlazemeterAnalyticsUrl,
  getBlazemeterApiUrl,
  isPrivateCloud,
  isReadOnlyInPrivateCloud,
} from 'utils/bzmUserConfigUtil';
import { clearAnalyticsTabs } from 'store/analytics/ui/analyticsUiActions';
import { initialiseDataContextForTabs } from 'store/analytics/data/analyticsDataActions';
import { saveWorkspace } from 'utils/analyticsUtils';
import { VIEW_AS_QUERY_PARAM } from 'constants/bzm-constants';
import blazeDataClient from 'blazeData/blazeDataClient';
import { clearDataModel } from '../dataModel/dataModelReducer';
import TagManager from 'react-gtm-module';
import { toggleOpenDDPanel } from '../ui/ddActions';
import acApi from '../../axios/ac/acApi';
import { getErrorMessage } from '../../utils/miscUtil';
import { fetchCertificatesThunk } from '../certificates/certificatesActions';
import { getConfigurationsListAction } from '../configurations/configurationsActions';
import { DevOptions, loadDevOptions } from '../../utils/localStorageUtil';
import { updateStartMockingPanel } from '../ui/startMockingBanner/startMockingBannerActions';
import { PendoData, pendoInit } from '../../helpers/pendoInit';
import { ANALYTICS_CONSENT_CATEGORY, runWhenConsented } from '../../helpers/consent';
import { getSecretsListAction } from '../secrets/secretsActions';

/**
 * Updates blazeMeterUser data in the store.
 */
export const updateBlazeUserConfig = (blazeMeterUser: BlazemeterUserConfig) =>
  action(actionTypes.UPDATE_CURRENT_USER_DATA, blazeMeterUser);

declare let PRELOADED_BLAZE_USER_CONFIG: BlazemeterUserConfig | null;

/**
 * Stores passed workspace to local storage and compares it with previous version which was
 * stored already. If id is different analytics tabs are cleared out.
 * @param bzmConfig
 * @param dispatch
 */
function cleanUpAnalyticsAndStoreWorkspaceId(bzmConfig: BlazemeterUserConfig, dispatch: any) {
  const wsId = localStorage.getItem('activeWorkspaceId');
  if (
    !_.isNil(wsId) &&
    !_.isNil(bzmConfig.user) &&
    !_.isNil(bzmConfig.user.activeWorkspaceId) &&
    bzmConfig.user.activeWorkspaceId.toString() !== wsId
  ) {
    dispatch(clearAnalyticsTabs());
  }
  saveWorkspace(bzmConfig.user.activeWorkspaceId.toString());
}

/**
 *  The `performInitialization` function initializes various parts of the application.
 *  This setup ensures that necessary data and services are ready for the application to function correctly.
 *
 * @param dispatch
 * @param bzmConfig
 */
export const performInitialization = async (
  dispatch: ThunkDispatch<ApplicationState, null, AnyAction>,
  bzmConfig: BlazemeterUserConfig,
) => {
  // Prefetch locations
  dispatch(getLocationsThunk());

  // Prefetch list of certificates
  dispatch(fetchCertificatesThunk(true, true));

  //Clean up analytic tabs and initialize data context
  await cleanUpAnalyticsAndStoreWorkspaceId(bzmConfig, dispatch);
  await initialiseDataContextForTabs();

  // Events pushed to GTM before it loads (it starts only after consent) are queued here and replayed once it does
  window.dataLayer = window.dataLayer || [];

  //Update mixpanel
  const mToken = bzmConfig.config.mixPanelToken;
  runWhenConsented(ANALYTICS_CONSENT_CATEGORY, () => {
    MixpanelService.init(mToken);
    MixpanelService.setUser(bzmConfig.user);
  });

  //Load configuration list
  dispatch(getConfigurationsListAction());

  //Load secrets list
  dispatch(getSecretsListAction());

  // Initialize GTM
  const tagManagerArgs = {
    gtmId: bzmConfig.config.gtmAccountId,
    auth: bzmConfig.config.gtmAuth,
    preview: bzmConfig.config.gtmPreview,
  };
  runWhenConsented(ANALYTICS_CONSENT_CATEGORY, () => TagManager.initialize(tagManagerArgs));

  const pendoData: PendoData = buildPendoData(bzmConfig);
  runWhenConsented(ANALYTICS_CONSENT_CATEGORY, () => pendoInit(pendoData));

  // Initialize Analytics Axios Instance
  analytics.defaults.baseURL = getBlazemeterAnalyticsUrl(bzmConfig);
};

/**
 * Fetch User config and perform init.
 */
export const fetchBlazeUserConfigThunk = (): ThunkAction<any, ApplicationState, null, AnyAction> => {
  return async (dispatch: ThunkDispatch<ApplicationState, null, AnyAction>, getState: any): Promise<void> => {
    try {
      let bzmConfig: BlazemeterUserConfig;

      if (PRELOADED_BLAZE_USER_CONFIG != null) {
        bzmConfig = PRELOADED_BLAZE_USER_CONFIG;
        PRELOADED_BLAZE_USER_CONFIG = null;
      } else {
        bzmConfig = (await axios.get('/user')).data.result;
      }
      // if view_as is active we need to append the query param to each URL
      if (getState().blazeUserConfig.viewAsUser) {
        _.forOwn(bzmConfig.config, (value, key) => {
          if (key.indexOf('Url') !== -1) {
            bzmConfig.config[key] = modifyUrlForViewAs(value, getState().blazeUserConfig.viewAsUser);
          }
        });
        _.forOwn(bzmConfig.config.menu, (value, key) => {
          bzmConfig.config.menu[key] = modifyUrlForViewAs(value, getState().blazeUserConfig.viewAsUser);
        });
      }

      const isReadOnly: boolean = isReadOnlyInPrivateCloud(bzmConfig);
      dispatch(setIsPrivateCloudReadOnly(isReadOnly));

      //Hide banner
      const devOptions: DevOptions | undefined = loadDevOptions();
      if (devOptions && devOptions.hideStartMockingBanner === true) {
        dispatch(updateStartMockingPanel(false));
      }

      //If we are not in private cloud, init blaze data client
      if (!isPrivateCloud(bzmConfig)) {
        blazeDataClient.createClient(bzmConfig.user.activeWorkspaceId);
        //Data panel should be open by default (US793522)
        dispatch(toggleOpenDDPanel());
      }

      // Update fetched user config, app will start to render after this step
      dispatch(updateBlazeUserConfig(bzmConfig));

      // Initialize app
      await performInitialization(dispatch, bzmConfig);
    } catch (error) {
      dispatch(
        showToastNotification(
          ToastType.Danger,
          i18n.t('errorMsgs.networkProblem'),
          i18n.t('errorMsgs.cannotFindUserInfo') + ' ' + getErrorMessage(error),
        ),
      );
    }
  };
};

/**
 * Modifies the URLs going outside Virtual Services to include the view_as parameter in the correct position
 * @param url - the url to modify
 * @param viewAs - view as user id
 */
const modifyUrlForViewAs = (url: string, viewAs: number): string => {
  const parsedURL = new URL(url);
  parsedURL.searchParams.append(VIEW_AS_QUERY_PARAM, '' + viewAs);
  return parsedURL.href;
};

export const switchWorkspaceAction = (workspaceId: number) => action(actionTypes.SWITCH_WORKSPACE, workspaceId);

/**
 * Action responsible for switch workspace action. All views and states should be rest after this action
 * takes place.
 * @param workspaceId
 */
export const switchWorkspaceThunk = (workspaceId: number): ThunkAction<any, ApplicationState, null, AnyAction> => {
  return async (dispatch: ThunkDispatch<ApplicationState, null, AnyAction>, getState: any): Promise<void> => {
    //Trigger change of the workspace in bzm
    dispatch(updateActiveWorkspaceInBzm(workspaceId));

    // Update bzm user config locally
    const bzmUserConfig: BlazemeterUserConfig = _.cloneDeep(getState().blazeUserConfig.blazeUserConfig);
    bzmUserConfig.user.activeWorkspaceId = workspaceId;
    saveWorkspace(workspaceId.toString());
    await dispatch(updateBlazeUserConfig(bzmUserConfig));
    await dispatch(switchWorkspaceAction(workspaceId));

    //Clear blaze data model
    dispatch(clearDataModel());

    // reload all views that rely on workspaceId
    dispatch(fetchServices(true));
    dispatch(getTransactions());
    dispatch(getMockServiceListThunk());
    dispatch(getMockServiceTemplates(undefined, true));
    dispatch(getLocationsThunk());
    dispatch(getMarFileListThunk());
    dispatch(clearAnalyticsTabs());
    dispatch(getConfigurationsListAction());
    dispatch(getSecretsListAction());
    dispatch(fetchCertificatesThunk(true, false));

    // update the BlazeData client to use new workspaceId
    blazeDataClient.createClient(workspaceId);

    // add more here as needed
    MixpanelService.track(WORKSPACE_CHANGED());
  };
};

/**
 * Update current Workspace: REST calls to update all views.
 */
export const reloadWorkspaceComponents = (
  selectedServiceId: number | undefined,
  oldServName?: string,
  newServName?: string,
): ThunkAction<any, ApplicationState, null, AnyAction> => {
  return async (dispatch: ThunkDispatch<ApplicationState, null, AnyAction>): Promise<void> => {
    // reload all views that rely on workspaceId
    // It gets all newly cloned txs and creates virtual service draft with those txs and display toast message.
    dispatch(fetchServices(true));
    dispatch(getConfigurationsListAction());
    dispatch(getSecretsListAction());
    if (selectedServiceId && oldServName && newServName) {
      await dispatch(getAllTransactionsThunk(selectedServiceId, oldServName, newServName));
      dispatch(getMockServiceListThunk());
    }
    dispatch(getTransactions());
    dispatch(getMockServiceListThunk());
    dispatch(getMockServiceTemplates(selectedServiceId, true));
    dispatch(getLocationsThunk());
    dispatch(getMarFileListThunk());
    dispatch(clearAnalyticsTabs());
  };
};

/**
 * Send patch request to Blazemeter to switch the Workspace everywhere
 * @param workspaceId
 */
export const updateActiveWorkspaceInBzm = (
  workspaceId: number,
): ThunkAction<any, ApplicationState, null, AnyAction> => {
  return async (dispatch: ThunkDispatch<ApplicationState, null, AnyAction>, getState: any): Promise<void> => {
    try {
      const bmConfig = getState().blazeUserConfig.blazeUserConfig as BlazemeterUserConfig;
      const bzmApiUrl = getBlazemeterApiUrl(bmConfig);
      await axios.patch(
        bzmApiUrl + 'user/preferences',
        {
          activeOrganizationId: null,
          activeProjectId: null,
          activeWorkspaceId: workspaceId,
        },
        { withCredentials: true },
      );
    } catch (error) {
      const errorBody = _.get(error, 'response.data.error', error.toString());
      dispatch(showToastNotification(ToastType.Danger, 'Failed to switch Workspace on Blazemeter', errorBody));
    }
  };
};

/**
 * Picks the workspace to activate when switching account: the first enabled workspace, or the first available one.
 * @param workspaces workspaces of the target account
 */
export const pickActiveWorkspace = (workspaces: any[]): any => {
  if (!workspaces || workspaces.length === 0) {
    return undefined;
  }
  return workspaces.find(workspace => workspace.enabled) || workspaces[0];
};

/**
 * Persist the active account (and a workspace belonging to it) on Blazemeter.
 * Blazemeter does not cascade account -> workspace, so both must be sent together.
 * @param accountId target account id
 * @param workspaceId a workspace id belonging to the target account
 */
export const updateActiveAccountInBzm = (
  accountId: number,
  workspaceId: number,
): ThunkAction<any, ApplicationState, null, AnyAction> => {
  return async (dispatch: ThunkDispatch<ApplicationState, null, AnyAction>, getState: any): Promise<void> => {
    const bmConfig = getState().blazeUserConfig.blazeUserConfig as BlazemeterUserConfig;
    const bzmApiUrl = getBlazemeterApiUrl(bmConfig);
    await axios.patch(
      bzmApiUrl + 'user/preferences',
      {
        activeAccountId: accountId,
        activeWorkspaceId: workspaceId,
        activeOrganizationId: workspaceId,
      },
      { withCredentials: true },
    );
  };
};

/**
 * Switches the active account. Because the available workspaces depend on the account, this performs a heavier
 * reset than the workspace switch: it selects a workspace in the target account, persists the switch on Blazemeter,
 * asks the asset-catalog backend to drop its cached account data and return a fresh config, then reloads every
 * account/workspace scoped view.
 * @param accountId target account id
 */
export const switchAccountThunk = (accountId: number): ThunkAction<any, ApplicationState, null, AnyAction> => {
  return async (dispatch: ThunkDispatch<ApplicationState, null, AnyAction>, getState: any): Promise<void> => {
    try {
      const bmConfig = getState().blazeUserConfig.blazeUserConfig as BlazemeterUserConfig;
      const bzmApiUrl = getBlazemeterApiUrl(bmConfig);

      // Blazemeter workspaces are account-scoped: fetch the target account's workspaces and pick one.
      const workspacesResponse = await axios.get(bzmApiUrl + 'workspaces', {
        params: { accountId, limit: 1000 },
        withCredentials: true,
      });
      const targetWorkspace = pickActiveWorkspace(workspacesResponse.data.result);
      if (!targetWorkspace) {
        dispatch(
          showToastNotification(
            ToastType.Danger,
            'Failed to switch Account',
            'No workspace is available in the selected account.',
          ),
        );
        return;
      }

      // Persist the account/workspace switch on Blazemeter.
      await dispatch(updateActiveAccountInBzm(accountId, targetWorkspace.id));

      // Ask the asset-catalog backend to evict cached auth data and return the fresh account context.
      const freshConfig: BlazemeterUserConfig = (await acApi.userDetails.refreshActiveAccount()).data.result;
      await dispatch(updateBlazeUserConfig(freshConfig));
      saveWorkspace(freshConfig.user.activeWorkspaceId.toString());

      // Switching account also changes the active workspace, so run the same workspace-scoped resets
      // (transactions, ui, ai conversations) that switchWorkspaceThunk triggers to avoid stale state.
      await dispatch(switchWorkspaceAction(freshConfig.user.activeWorkspaceId));

      // Clear blaze data model.
      dispatch(clearDataModel());

      // Reload all views that rely on the active account / workspace.
      dispatch(fetchServices(true));
      dispatch(getTransactions());
      dispatch(getMockServiceListThunk());
      dispatch(getMockServiceTemplates(undefined, true));
      dispatch(getLocationsThunk());
      dispatch(getMarFileListThunk());
      dispatch(clearAnalyticsTabs());
      dispatch(getConfigurationsListAction());
      dispatch(getSecretsListAction());
      dispatch(fetchCertificatesThunk(true, false));

      // Update the BlazeData client to use the new workspaceId.
      blazeDataClient.createClient(freshConfig.user.activeWorkspaceId);

      MixpanelService.track(ACCOUNT_CHANGED());
    } catch (error) {
      dispatch(
        showToastNotification(ToastType.Danger, 'Failed to switch Account on Blazemeter', getErrorMessage(error)),
      );
    }
  };
};

export const setVisitedMockServicesThunk = (): ThunkAction<any, ApplicationState, null, AnyAction> => {
  return async (dispatch: ThunkDispatch<ApplicationState, null, AnyAction>): Promise<void> => {
    dispatch(setVisitedMockServicesAction());
    await acApi.userDetails.patchPreferences({ visitedMockServices: true });
  };
};

export const setVisitedMockServicesAction = () => action(actionTypes.SET_VISITED_MS_PAGE);

export const setViewAsUser = (viewAsUser: number) => action(actionTypes.SET_VIEW_AS_USER, viewAsUser);

export const setIsPrivateCloudReadOnly = (readOnly: boolean) =>
  action(actionTypes.SET_IS_PRIVATE_CLOUD_READ_ONLY, readOnly);

export const clearViewAsUser = () => action(actionTypes.CLEAR_VIEW_AS_USER);
